import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipFrame, clipOutroFade, combineSceneFragments, makeTl, typeSize, weights, withAlpha,
  colors as themeColors, escapeHtml, gsapSetLine,
} from "../../reel/sceneShared.js";

const STAGGER_SEC = 5 / 30;

/**
 * Motion-blur reveal — Remotion uses `@remotion/motion-blur` with stacked
 * time-shifted copies. In Hyperframes we approximate via CSS `filter: blur()`
 * keyframed off to 0 alongside the X tween. Visually close, much simpler.
 */
export const motionBlurReveal: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const text = inputs.text ?? "Stopp! Du verlierst Geld.";
  const accent = accentFor(inputs);
  const hero = typeSize("hero", ctx.format);
  const words = text.split(/\s+/).filter(Boolean);
  const accentIdx = Math.floor(words.length / 2);

  const t = makeTl(clipStartSec);

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const wordSpans = words
    .map((w, i) => {
      const isAccent = i === accentIdx;
      const color = isAccent ? `color:${accent};text-shadow:0 0 32px ${withAlpha(accent, 0.55)};` : `text-shadow:0 6px 28px ${withAlpha("#000", 0.4)};`;
      return `<span id="${clipPrefix}-w-${i}" style="display:inline-block;margin-right:.25em;${color}">${escapeHtml(w)}</span>`;
    })
    .join("");

  const inner = `<h1 style="margin:0;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${hero * 0.92}px;line-height:.95;letter-spacing:-3.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${ctx.format === "9:16" ? 920 : 1500}px;">${wordSpans}</h1>`;
  const html = clipFrame({ clipPrefix, clipStartSec, clipDurationSec, inner });

  const init = words
    .map((_, i) => {
      const dir = i % 2 === 0 ? -1 : 1;
      return gsapSetLine(`#${clipPrefix}-w-${i}`, { opacity: 0, x: dir * 280, filter: "blur(14px)" });
    })
    .join("\n");

  const tweens = words.map((_, i) =>
    t.fromTo(
      `#${clipPrefix}-w-${i}`,
      { opacity: 0, x: (i % 2 === 0 ? -1 : 1) * 280, filter: "blur(14px)" },
      { opacity: 1, x: 0, filter: "blur(0px)", duration: 0.73, ease: "raw:\"expo.out\"" },
      i * STAGGER_SEC,
    ),
  );
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
